import { useState, useEffect, useCallback, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import { useDebouncedCallback } from "use-debounce";
import { FIND_RELEVANT_MATTERS_QUERY } from "~/graphql/query";
import { MatterFilter } from "~/design-system/data-types/matter-filter";

const DEFAULT_DISTANCE_THRESHOLD = 0.85;
const RETRY_DISTANCE_THRESHOLD = 1.0;

export type UseRelevantMatterSearchOptions = {
  additionalSearchText?: string;
  autoRetry?: boolean;
  fetchTemplates?: boolean;
  excludeMatterId?: number;
  fetchAccruedFees?: boolean;
};

const transformMatterFilter = (
  filter: MatterFilter,
  isTemplate: boolean,
  excludeMatterId?: number
) => {
  return {
    template: isTemplate,
    title: filter.title,
    organisationalUnitId: filter.orgUnit,
    ...(excludeMatterId && { excludeMatterId }),
    ...(isTemplate
      ? {}
      : {
          clientId: filter.client,
          status: filter.status,
          updatedAtFrom: filter.updatedAt?.from,
          updatedAtTo: filter.updatedAt?.to,
          currencyId: filter.currencyId,
          feeEarnerId: filter.feeEarner,
          someTimeEntries: filter.someTimeEntries,
        }),
  };
};

export const useRelevantMatterSearch = (
  searchText: string,
  matterFilter: MatterFilter,
  options: UseRelevantMatterSearchOptions = {}
) => {
  // Add refs to track previous values of all parameters that could trigger a search
  const prevSearchTextRef = useRef(searchText);
  const prevMatterFilterOrgUnitRef = useRef(matterFilter.orgUnit);
  const prevAdditionalSearchTextRef = useRef<string | null | undefined>(
    options.additionalSearchText
  );

  const {
    additionalSearchText = null,
    autoRetry = true,
    fetchTemplates = false,
    excludeMatterId,
  } = options;
  const [historicalDistanceThreshold, setHistoricalDistanceThreshold] = useState(
    DEFAULT_DISTANCE_THRESHOLD
  );
  const [templatesDistanceThreshold, setTemplatesDistanceThreshold] = useState(
    DEFAULT_DISTANCE_THRESHOLD
  );

  const [
    fetchHistoricalQuery,
    {
      data: historicalData,
      loading: historicalLoading,
      error: historicalError,
      refetch: refetchHistorical,
    },
  ] = useLazyQuery(FIND_RELEVANT_MATTERS_QUERY);

  const [
    fetchTemplatesQuery,
    {
      data: templatesData,
      loading: templatesLoading,
      error: templatesError,
      refetch: refetchTemplates,
    },
  ] = useLazyQuery(FIND_RELEVANT_MATTERS_QUERY);

  // Add a counter for debounced search calls
  const debouncedCallCount = useRef(0);

  const debouncedSearch = useDebouncedCallback(() => {
    debouncedCallCount.current += 1;

    if (!searchText.trim()) {
      return;
    }

    if (fetchTemplates) {
      fetchTemplatesQuery({
        variables: {
          text: searchText,
          filter: transformMatterFilter(matterFilter, true, excludeMatterId),
          fetchAccruedFees: false,
          distanceThreshold: templatesDistanceThreshold,
          maxResults: 50,
          additionalSearchText: additionalSearchText?.trim() || null,
        },
      });
      fetchHistoricalQuery({
        variables: {
          text: searchText,
          filter: transformMatterFilter(matterFilter, false, excludeMatterId),
          fetchAccruedFees: options.fetchAccruedFees ?? true,
          distanceThreshold: historicalDistanceThreshold,
          maxResults: 50,
          additionalSearchText: additionalSearchText?.trim() || null,
        },
      });
    } else {
      fetchHistoricalQuery({
        variables: {
          text: searchText,
          filter: transformMatterFilter(matterFilter, false, excludeMatterId),
          fetchAccruedFees: options.fetchAccruedFees ?? true,
          distanceThreshold: historicalDistanceThreshold,
          maxResults: 50,
          additionalSearchText: additionalSearchText?.trim() || null,
        },
      });
    }
  }, 300);

  // Run the query whenever search text, filter, or distance threshold changes.
  useEffect(() => {
    // Update refs for next comparison
    prevSearchTextRef.current = searchText;
    prevMatterFilterOrgUnitRef.current = matterFilter.orgUnit;
    prevAdditionalSearchTextRef.current = additionalSearchText;

    // Run the search if we have search text, even on first render
    if (searchText.trim()) {
      debouncedSearch();
    }

    return () => {};
  }, [
    searchText,
    matterFilter,
    additionalSearchText,
    historicalDistanceThreshold,
    templatesDistanceThreshold,
    debouncedSearch,
  ]);

  // Auto-retry logic: if autoRetry is enabled and the result is empty, then expand the distance threshold.
  useEffect(() => {
    if (!autoRetry) return;

    if (
      historicalData &&
      historicalData.findRelevantMatters &&
      historicalData.findRelevantMatters.matters.length === 0 &&
      historicalDistanceThreshold === DEFAULT_DISTANCE_THRESHOLD
    ) {
      setHistoricalDistanceThreshold(RETRY_DISTANCE_THRESHOLD);
    }

    if (
      fetchTemplates &&
      templatesData &&
      templatesData.findRelevantMatters &&
      templatesData.findRelevantMatters.matters.length === 0 &&
      templatesDistanceThreshold === DEFAULT_DISTANCE_THRESHOLD
    ) {
      setTemplatesDistanceThreshold(RETRY_DISTANCE_THRESHOLD);
    }
  }, [
    autoRetry,
    fetchTemplates,
    historicalData,
    templatesData,
    historicalDistanceThreshold,
    templatesDistanceThreshold,
  ]);

  // unified refetch method.
  const refetch = useCallback(() => {
    if (fetchTemplates) {
      refetchTemplates();
      refetchHistorical();
    } else {
      refetchHistorical();
    }
  }, [fetchTemplates, refetchTemplates, refetchHistorical]);

  return {
    historicalDistanceThreshold,
    templatesDistanceThreshold: fetchTemplates ? templatesDistanceThreshold : undefined,
    templatesData: fetchTemplates ? templatesData : undefined,
    templatesLoading: fetchTemplates ? templatesLoading : undefined,
    templatesError: fetchTemplates ? templatesError : undefined,
    historicalData,
    historicalLoading,
    historicalError,
    refetch,
    triggerSearch: debouncedSearch,
  };
};
